/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ComponentProps, type KeyboardEvent, type MouseEvent } from 'react';
import { css, cssMap, jsx, cx } from '@atlaskit/css';
import Tooltip from '@atlaskit/tooltip';

import { token } from '@atlaskit/tokens';
import { useIntl } from 'react-intl-next';
import messages from './messages';
import { Pressable } from '@atlaskit/primitives/compiled';

type GrabAreaProps = {
	isCollapsed: boolean;
	isMaxWidth: boolean;
	onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
	onMouseDown: (event: MouseEvent<HTMLButtonElement>) => void;
	onBlur: (event: FocusEvent) => void;
	onFocus: (event: FocusEvent) => void;
	orgSelectorPercentageExpanded: number;
	isResizing: boolean;
};
let varLineColor = '--ds-background-neutral-subtle';

const grabAreaStyles = cssMap({
	root: {
		width: '8px',
		position: 'absolute',
		height: '100%',
		padding: 0,
		backgroundColor: 'transparent',
		border: 'none',
		'&:hover': {
			[varLineColor]: token('color.background.selected.bold', '#0C66E4'),
		},
		'&:focus': {
			[varLineColor]: token('color.background.selected.bold', '#0C66E4'),
			outline: 0,
		},
		'&:active': {
			[varLineColor]: token('color.background.selected.bold', '#0C66E4'),
		},
	},
	before: {
		content: '""',
		position: 'absolute',
		left: token('space.negative.100', '-8px'),
		top: token('space.0', '0px'),
		bottom: token('space.0', '0px'),
		width: '8px',
	},
	inBetween: {
		cursor: 'ew-resize',
	},
	collapsed: {
		cursor: 'e-resize',
		padding: 0,
		backgroundColor: 'transparent',
		border: 'none',
	},
	maxWidth: {
		cursor: 'w-resize',
	},
});

const lineStyles = css({
	display: 'block',
	width: '2px',
	height: '100%',
	backgroundColor: token('color.background.neutral.subtle', 'transparent'),
	transition: 'background-color 200ms',
});

const GrabArea = ({
	isCollapsed,
	isMaxWidth,
	onKeyDown,
	onMouseDown,
	onFocus,
	onBlur,
	orgSelectorPercentageExpanded,
	isResizing,
}: GrabAreaProps & ComponentProps<'button'>) => {
	const { formatMessage } = useIntl();

	return (
		<Tooltip
			content={
				isResizing
					? null
					: formatMessage(
							isCollapsed ? messages.resizeBarTooltipCollapsed : messages.resizeBarTooltipExpanded,
							{
								separator: <br />,
							},
						)
			}
			position="mouse"
			mousePosition="right"
			delay={1000}
			hideTooltipOnMouseDown
			hideTooltipOnClick
		>
			{
				// we omit aria-describedby to prevent button label from overriding
				({ ref: tooltipRef, 'aria-describedby': _, ...tooltipProps }) => (
					<Pressable
						{...tooltipProps}
						aria-label={formatMessage(messages.resizeBarLabel)}
						type="button"
						// The slider role is applied to a button to utilize the native
						// interactive and disabled functionality on the resize slider. While a
						// range input would be more semantically accurate, it does not affect
						// usability.
						role="slider"
						xcss={cx(
							grabAreaStyles.root,
							!isCollapsed && !isMaxWidth && grabAreaStyles.inBetween,
							isCollapsed && grabAreaStyles.collapsed,
							isMaxWidth && grabAreaStyles.maxWidth,
						)}
						ref={tooltipRef}
						aria-orientation="vertical"
						aria-valuenow={orgSelectorPercentageExpanded}
						aria-valuemin={0}
						aria-valuemax={100}
						aria-valuetext={`${formatMessage(messages.resizeBarValueTextLabel)} ${orgSelectorPercentageExpanded}%`}
						onKeyDown={onKeyDown}
						onMouseDown={onMouseDown}
						onFocus={onFocus}
						onBlur={onBlur}
						testId="org-selector-resize-bar"
					>
						<div css={grabAreaStyles.before} />
						<span css={lineStyles} />
					</Pressable>
				)
			}
		</Tooltip>
	);
};

export default GrabArea;
